import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type SpecialOfferBannerContent = {
  title: string;
  primaryDescription: string;
  additionalDescriptions: Array<string>;
};

type OfferCandidate = {
  description: string;
  title: string | null;
  priority: number;
  unsafe: boolean;
};

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
};

const MAX_FREE_MONTHS = 3;
const MAX_FREE_WEEKS = 12;
const MAX_RENT_DISCOUNT = 10000;
const MIN_TITLE_CASE_WORDS = 4;
const TITLE_CASE_RATIO = 0.85;

function normalizeDescriptionCase(value: string): string {
  const words = value.match(/[A-Za-z]+(?:['’-][A-Za-z]+)*/g) ?? [];
  if (words.length < MIN_TITLE_CASE_WORDS) return value;

  const titleCaseWords = words.filter((word) => /^[A-Z](?:[a-z]+|[A-Z]+)$/.test(word));
  if (titleCaseWords.length / words.length < TITLE_CASE_RATIO) return value;

  let wordIndex = 0;
  return value.replace(/[A-Za-z]+(?:['’-][A-Za-z]+)*/g, (word) => {
    const normalized = word.toLocaleLowerCase();
    const result = wordIndex === 0 ? `${normalized[0].toLocaleUpperCase()}${normalized.slice(1)}` : normalized;
    wordIndex += 1;
    return result;
  });
}

function cleanDescription(value: unknown): string {
  return typeof value === 'string' ? normalizeDescriptionCase(value.trim().replace(/\s+/g, ' ')) : '';
}

function getRawDescriptions(listing: PropertyDetailListing): Array<string> {
  const details = listing.details as
    | {
        specialOffer?: unknown;
        specialOffers?: Array<{ description?: unknown }>;
      }
    | undefined;
  const rootOffers = (listing as { specialOffers?: Array<{ description?: unknown }> }).specialOffers;
  const values = [
    ...(details?.specialOffers ?? []).map((offer) => offer.description),
    ...(rootOffers ?? []).map((offer) => offer.description),
    details?.specialOffer,
    (listing as { specialOfferLabel?: unknown }).specialOfferLabel,
  ];
  const seen = new Set<string>();

  const descriptions = values.flatMap((value) => {
    const description = cleanDescription(value);
    if (description.toLocaleLowerCase() === 'special offer') return [];
    const comparison = description.toLocaleLowerCase();
    if (!description || seen.has(comparison)) return [];
    seen.add(comparison);
    return [description];
  });

  return descriptions.filter((description, index) => {
    const normalized = description.toLocaleLowerCase();
    return !descriptions.slice(0, index).some((earlier) => earlier.toLocaleLowerCase().includes(normalized));
  });
}

function parseQuantity(value: string): number | null {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  return NUMBER_WORDS[value.toLocaleLowerCase()] ?? null;
}

function classifyOffer(description: string): OfferCandidate {
  const normalized = description.toLocaleLowerCase();
  const isQualified = /\b(up to|as much as|may receive)\b/i.test(description);
  const freeRentMatch = normalized.match(/\b(one|two|three|\d+(?:\.\d+)?)\s+(month|week)s?\s+(?:of\s+)?free\b/);

  if (freeRentMatch) {
    const quantity = parseQuantity(freeRentMatch[1]);
    const unit = freeRentMatch[2];
    const maximum = unit === 'month' ? MAX_FREE_MONTHS : MAX_FREE_WEEKS;
    if (quantity == null || quantity <= 0 || quantity > maximum) {
      return { description, title: null, priority: 0, unsafe: true };
    }
    if (isQualified) return { description, title: null, priority: 0, unsafe: false };

    const formattedQuantity = String(quantity);
    const formattedUnit = `${unit[0].toLocaleUpperCase()}${unit.slice(1)}${quantity === 1 ? '' : 's'}`;
    return {
      description,
      title: `${formattedQuantity} ${formattedUnit} Free`,
      priority: 2,
      unsafe: false,
    };
  }

  const discountMatch = normalized.match(
    /\$([\d,]+(?:\.\d{1,2})?)\s+off\s+(?:your\s+)?(?:first\s+month(?:'s)?\s+)?rent\b/,
  );
  if (discountMatch) {
    const amount = Number(discountMatch[1].replaceAll(',', ''));
    if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_RENT_DISCOUNT) {
      return { description, title: null, priority: 0, unsafe: true };
    }
    if (isQualified) return { description, title: null, priority: 0, unsafe: false };

    return {
      description,
      title: `$${amount.toLocaleString('en-US')} Off Rent`,
      priority: 1,
      unsafe: false,
    };
  }

  return { description, title: null, priority: 0, unsafe: false };
}

export function getSpecialOfferBannerContent(
  listing: PropertyDetailListing,
  genericTitle: string,
): SpecialOfferBannerContent | null {
  const descriptions = getRawDescriptions(listing);
  const hasOfferSignal = Boolean(
    listing.hasSpecialOffers ||
    descriptions.length > 0 ||
    cleanDescription((listing as { specialOfferLabel?: unknown }).specialOfferLabel),
  );
  if (!hasOfferSignal) return null;

  const candidates = descriptions.map(classifyOffer).filter((candidate) => !candidate.unsafe);
  const ordered = candidates
    .map((candidate, index) => ({ ...candidate, index }))
    .sort((a, b) => b.priority - a.priority || a.index - b.index);
  const primary = ordered[0];

  const translatedDescription = (description: string) =>
    listing.specialOfferTranslations?.find((offer) => cleanDescription(offer.englishDescription) === description)
      ?.translatedDescription;
  const primaryTranslation = primary ? translatedDescription(primary.description) : undefined;

  return {
    title: primaryTranslation ? genericTitle : (primary?.title ?? genericTitle),
    primaryDescription: primaryTranslation ?? primary?.description ?? '',
    additionalDescriptions: ordered
      .slice(1)
      .map((candidate) => translatedDescription(candidate.description) ?? candidate.description),
  };
}
